import { useQuery } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { QueryConfig } from '../api/client';
import { driverApi, DriverProfile } from '../services/driverApi';
import { ensureSelectedCompanyId } from './userCompanies.shared';

/**
 * The signed-in driver's profile, after making sure a company is selected.
 * The splash and the profile query both start here, so a fresh device gets
 * a company before either asks.
 */
export const getDriverProfile = async (
  token: string
): Promise<DriverProfile> => {
  driverApi.setAuthToken(token);
  // The profile is a company route, so a company has to be chosen first.
  const companyId = await ensureSelectedCompanyId(token);
  try {
    return await driverApi.getProfile();
  } catch (error) {
    // A stored company the user no longer belongs to is refused too; pick
    // again from the companies they are in, and ask once more if that changed.
    if ((error as { status?: number }).status !== 403) throw error;
    const reselectedId = await ensureSelectedCompanyId(token, {
      refresh: true,
    });
    if (!reselectedId || reselectedId === companyId) throw error;
    return driverApi.getProfile();
  }
};

export const useDriverProfile = (
  config?: QueryConfig<typeof getDriverProfile>
) => {
  const { getAccessTokenSilently, isAuthenticated } = useHybridAuth();

  return useQuery({
    ...config,
    queryFn: async () => {
      console.log('👤 [useDriverProfile] Query function executing...');
      const token = await getAccessTokenSilently();
      console.log(
        '👤 [useDriverProfile] Got token, calling getDriverProfile...'
      );
      return getDriverProfile(token);
    },
    queryKey: ['driver-profile'],
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes for profile data (reduced from 1 hour for better freshness)
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchInterval: 15 * 60 * 1000, // Refetch profile every 15 minutes
  });
};
